/* Shared A4 payment flyer generator (single source of truth for the flyer design).
   Used by the Business ERP sub-accounts view and the Workspace division view.
   Preserves the original A4 design and overlays the PayMig + business logo. */
import { toast } from 'sonner';
import { PAYMIG_LOGO_B64 } from './paymigLogo';

const formatFlyerDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

// Escape user-provided strings before interpolating into the flyer HTML template.
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

/**
 * Build and download the A4 flyer for a sub-account/event.
 * @param {object} sa - sub-account (name, description, paymig_id, display_paymig_id, start_date, end_date, payment_url)
 * @param {object} opts - { bizName, bizLogo, paymentUrl, successMessage }
 */
export async function downloadFlyer(sa, opts = {}) {
  const bizName = opts.bizName || 'Business';
  const bizLogo = opts.bizLogo || '';
  const pmId = sa.display_paymig_id || sa.paymig_id;
  const qrValue = opts.paymentUrl || sa.payment_url || `${window.location.origin}/pay/${sa.paymig_id}`;

  // Display link at the bottom: match the QR domain so the flyer is self-consistent.
  let appOrigin = '';
  try { appOrigin = new URL(qrValue).origin; } catch { appOrigin = window.location.origin; }
  const appLinkText = appOrigin.replace(/^https?:\/\//, '');

  // Generate QR with brand-teal colors matching the design
  let qrDataUrl = '';
  try {
    const { default: QRCodeLib } = await import('qrcode');
    qrDataUrl = await QRCodeLib.toDataURL(qrValue, {
      width: 280,
      margin: 2,
      color: { dark: '#0D9488', light: '#F0FDFA' },
      errorCorrectionLevel: 'H',
    });
  } catch {
    qrDataUrl = '';
  }

  const startStr = formatFlyerDate(sa.start_date);
  const endStr = formatFlyerDate(sa.end_date);
  const dateRange = startStr && endStr ? `${startStr} - ${endStr}` : startStr || '';

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>PayMig - ${esc(sa.name)}</title>
<style>
  @page { size: A4; margin: 0 }
  * { box-sizing: border-box; margin: 0; padding: 0 }
  body {
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    background: #fff;
    color: #1a1a2e;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    padding: 28mm 30mm 20mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }

  /* Header: Logo + PayMig */
  .header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 12px;
  }
  .logo-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .logo-icon img { width: 100%; height: 100%; object-fit: cover; }
  .brand-text {
    font-size: 42px;
    font-weight: 800;
    color: #0D9488;
    letter-spacing: -0.5px;
  }

  /* Teal divider */
  .divider {
    width: 70px;
    height: 3px;
    background: #0D9488;
    border-radius: 2px;
    margin: 10px 0 20px;
  }
  .divider-dark {
    width: 120px;
    height: 3px;
    background: #1a1a2e;
    border-radius: 2px;
    margin: 20px 0;
  }

  /* Business name */
  .biz-name {
    font-size: 30px;
    font-weight: 800;
    color: #1a1a2e;
    text-align: center;
    margin-bottom: 6px;
  }

  /* Sub-account type badge */
  .sub-type {
    font-size: 14px;
    font-weight: 700;
    color: #0D9488;
    text-transform: uppercase;
    letter-spacing: 4px;
    margin-bottom: 4px;
  }

  /* Date range */
  .dates {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  /* Sub-account name */
  .sub-name {
    font-size: 24px;
    font-weight: 700;
    color: #1a1a2e;
    text-align: center;
    margin-bottom: 16px;
  }

  /* QR Section */
  .qr-section {
    padding: 28px 32px;
    background: #F0FDFA;
    border-radius: 20px;
    text-align: center;
    margin: 8px 0 12px;
    position: relative;
  }
  .qr-section img {
    width: 280px;
    height: 280px;
    display: block;
    margin: 0 auto;
  }
  .qr-section .qr-placeholder {
    width: 280px;
    height: 280px;
    background: #e0f2f1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    border-radius: 12px;
    color: #999;
    font-size: 14px;
  }
  /* PM logo overlay on QR */
  .qr-logo-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 56px;
    height: 56px;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    padding: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .qr-logo-overlay img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; }
  .scan-label {
    font-size: 18px;
    font-weight: 600;
    color: #0D9488;
    margin-top: 12px;
  }

  /* Instruction text */
  .instruction {
    font-size: 16px;
    color: #374151;
    text-align: center;
    margin-top: 16px;
  }

  /* app link */
  .app-link {
    font-size: 20px;
    font-weight: 800;
    color: #0D9488;
    text-decoration: none;
    margin: 12px 0 16px;
  }

  /* PayMig ID section */
  .paymig-id-section {
    display: flex;
    align-items: center;
    gap: 20px;
    margin: 8px 0 0;
  }
  .id-logo {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .id-logo img { width: 100%; height: 100%; object-fit: cover; }
  .id-text {
    display: flex;
    flex-direction: column;
  }
  .id-label {
    font-size: 22px;
    font-weight: 700;
    color: #0D9488;
    letter-spacing: 3px;
  }
  .id-value {
    font-size: 38px;
    font-weight: 800;
    color: #1a1a2e;
    font-family: 'Courier New', monospace;
    letter-spacing: 1px;
  }

  /* Footer */
  .footer {
    margin-top: auto;
    text-align: center;
    padding-top: 28px;
  }
  .footer p {
    font-size: 11px;
    color: #9ca3af;
    font-style: italic;
    line-height: 1.6;
  }

  @media print { .page { padding: 20mm 25mm 15mm } }
  .biz-logo {
    width: 84px; height: 84px; border-radius: 18px; overflow: hidden;
    background: #fff; border: 1px solid #e5e7eb; display: flex;
    align-items: center; justify-content: center; margin-bottom: 14px;
    box-shadow: 0 4px 14px rgba(0,0,0,.06);
  }
  .biz-logo img { width: 100%; height: 100%; object-fit: contain; }
</style></head><body>
<div class="page">

  <!-- Header: Logo + PayMig -->
  <div class="header">
    <div class="logo-icon">
      <img src="${PAYMIG_LOGO_B64}" alt="PayMig" />
    </div>
    <div class="brand-text">PayMig</div>
  </div>

  <!-- Teal divider -->
  <div class="divider"></div>

  <!-- Business logo -->
  ${bizLogo ? `<div class="biz-logo"><img src="${bizLogo}" alt="logo" /></div>` : ''}

  <!-- Business Name -->
  <div class="biz-name">${esc(bizName)}</div>

  <!-- Date range -->
  ${dateRange ? `<div class="dates">${esc(dateRange)}</div>` : ''}

  <!-- Sub-account name -->
  <div class="sub-name">${esc(sa.name)}</div>

  <!-- Description (large font) -->
  ${sa.description ? `<div style="font-size:18px;color:#374151;text-align:center;margin-bottom:16px;max-width:80%;line-height:1.4">${esc(sa.description)}</div>` : ''}

  <!-- QR Code Section -->
  <div class="qr-section">
    ${qrDataUrl
      ? `<img src="${qrDataUrl}" alt="QR Code" />`
      : `<div class="qr-placeholder">QR Code</div>`
    }
    <div class="qr-logo-overlay">
      <img src="${PAYMIG_LOGO_B64}" alt="PM" />
    </div>
    <div class="scan-label">Scan to pay</div>
  </div>

  <!-- Dark divider -->
  <div class="divider-dark"></div>

  <!-- Instruction text (bilingual) -->
  <div class="instruction">Scan QR code with mobile camera or PayMig app</div>
  <div class="instruction" style="color:#6b7280;font-size:14px;margin-top:4px">Skanna QR-koden med mobilkameran eller PayMig-appen</div>

  <!-- app link -->
  <a href="${esc(appOrigin)}" class="app-link">${esc(appLinkText)}</a>

  <!-- PayMig ID Section -->
  <div class="paymig-id-section">
    <div class="id-logo">
      <img src="${PAYMIG_LOGO_B64}" alt="PayMig" />
    </div>
    <div class="id-text">
      <div class="id-label">PayMig ID</div>
      <div class="id-value">${esc(pmId)}</div>
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <p>Swish&reg; is a registered trademark of Getswish AB.</p>
    <p>PayMig is a separate service built with the official resources from Getswish AB.</p>
  </div>

</div>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `PayMig_Flyer_${(sa.name || 'event').replace(/\s+/g, '_')}.html`;
  link.click();
  // Defer revoke so the browser has time to start the download (avoids a race on some browsers)
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  if (opts.successMessage) toast.success(opts.successMessage);
}
